import { useState } from 'react';
import { Search, Check, X, ChevronRight, Download, ExternalLink, User, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';

const DIMENSIONS = ['REG', 'PRI', 'SEC', 'INC', 'GOV', 'ENABLE'];
const SEVERITIES = ['High', 'Medium', 'Low'];

const TASKS = [
  {
    id: 1,
    task: 'No active tasks',
    dimension: 'N/A',
    severity: 'N/A',
    due: 'N/A',
    status: 'N/A',
    description: 'Complete an assessment to generate remediation tasks.',
    evidence: [],
    changelog: [
      { date: 'N/A', action: 'No tasks available', by: 'System' }
    ]
  }
];

export default function LiveRemediationActions() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDimensions, setSelectedDimensions] = useState([]);
  const [selectedSeverities, setSelectedSeverities] = useState([]);
  const [showClosed, setShowClosed] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showToast, setShowToast] = useState(false);

  const filteredTasks = TASKS.filter(task => {
    const matchesSearch = task.task.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDimension = selectedDimensions.length === 0 || selectedDimensions.includes(task.dimension);
    const matchesSeverity = selectedSeverities.length === 0 || selectedSeverities.includes(task.severity);
    const matchesStatus = showClosed || task.status === 'Open';
    return matchesSearch && matchesDimension && matchesSeverity && matchesStatus;
  });

  const handleResolveTask = (taskId) => {
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
    setSelectedTask(null);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'High': return 'bg-red-600';
      case 'Medium': return 'bg-yellow-500';
      case 'Low': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Live Remediation Actions</h1>
        <p className="text-text-secondary">
          Complete an assessment to track and manage tasks for improving your AI Maturity score and maintaining compliance.
        </p>
      </div>

      {/* Empty State */}
      <div className="bg-background-light rounded-xl border border-primary/20 p-8 text-center">
        <div className="max-w-md mx-auto">
          <div className="bg-primary/10 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <FileText className="text-primary" size={24} />
          </div>
          <h3 className="text-xl font-semibold mb-2">No Remediation Tasks Available</h3>
          <p className="text-text-secondary mb-6">
            Run an assessment to identify areas for improvement and generate actionable tasks.
          </p>
          <button className="bg-primary text-background px-6 py-2 rounded-lg hover:bg-primary-light transition-colors">
            Start Assessment
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" size={20} />
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-background-light border border-primary/20 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        <select
          value={selectedDimensions[0] || ''}
          onChange={(e) => setSelectedDimensions(e.target.value ? [e.target.value] : [])}
          className="px-4 py-2 bg-background-light border border-primary/20 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
        >
          <option value="">All Dimensions</option>
          {DIMENSIONS.map(dim => (
            <option key={dim} value={dim}>{dim}</option>
          ))}
        </select>
        <div className="flex gap-2">
          {SEVERITIES.map(severity => (
            <button
              key={severity}
              onClick={() => {
                setSelectedSeverities(prev => 
                  prev.includes(severity)
                    ? prev.filter(s => s !== severity)
                    : [...prev, severity]
                );
              }}
              className={`px-3 py-1 rounded-full text-sm ${
                selectedSeverities.includes(severity)
                  ? `${getSeverityColor(severity)} text-background`
                  : 'bg-background-light text-text-secondary'
              }`}
            >
              {severity}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={showClosed}
            onChange={(e) => setShowClosed(e.target.checked)}
            className="rounded border-primary/20"
          />
          <span className="text-sm">Show closed</span>
        </label>
      </div>

      {/* Tasks Table */}
      <div className="bg-background-light rounded-lg border border-primary/20 overflow-hidden">
        <table className="w-full">
          <thead className="bg-background-light border-b border-primary/20 sticky top-0">
            <tr>
              <th className="p-4 text-left">
                <input
                  type="checkbox"
                  checked={selectedTasks.length === filteredTasks.length}
                  onChange={(e) => {
                    setSelectedTasks(e.target.checked ? filteredTasks.map(t => t.id) : []);
                  }}
                  className="rounded border-primary/20"
                />
              </th>
              <th className="p-4 text-left">Task</th>
              <th className="p-4 text-left">Dimension</th>
              <th className="p-4 text-left">Severity</th>
              <th className="p-4 text-left">Due</th>
              <th className="p-4 text-left">Status</th>
              <th className="p-4 text-left">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredTasks.map(task => (
              <tr
                key={task.id}
                className={`border-b border-primary/10 last:border-0 hover:bg-primary/5 cursor-pointer ${
                  task.status === 'Closed' ? 'opacity-50' : ''
                }`}
                onClick={() => setSelectedTask(task)}
              >
                <td className="p-4">
                  <input
                    type="checkbox"
                    checked={selectedTasks.includes(task.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      setSelectedTasks(prev =>
                        prev.includes(task.id)
                          ? prev.filter(id => id !== task.id)
                          : [...prev, task.id]
                      );
                    }}
                    className="rounded border-primary/20"
                  />
                </td>
                <td className="p-4">{task.task}</td>
                <td className="p-4">{task.dimension}</td>
                <td className="p-4">
                  <span className={`px-2 py-1 rounded-full text-sm ${getSeverityColor(task.severity)} text-background`}>
                    {task.severity}
                  </span>
                </td>
                <td className="p-4">{task.due}</td>
                <td className="p-4">{task.status}</td>
                <td className="p-4">
                  {task.status === 'Open' ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleResolveTask(task.id);
                      }}
                      className="text-primary hover:text-primary-light"
                    >
                      Resolve
                    </button>
                  ) : (
                    <span className="text-text-secondary">Resolved</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Bulk Actions */}
      {selectedTasks.length > 0 && (
        <div className="mt-4 flex items-center gap-4">
          <span className="text-sm text-text-secondary">
            {selectedTasks.length} task{selectedTasks.length > 1 ? 's' : ''} selected
          </span>
          <button className="px-4 py-2 bg-primary text-background rounded-lg hover:bg-primary-light transition-colors">
            Mark Resolved
          </button>
          <button className="px-4 py-2 bg-background-light text-text rounded-lg hover:bg-primary/10 transition-colors flex items-center gap-2">
            <Download size={16} />
            Export CSV
          </button>
          <button className="px-4 py-2 bg-background-light text-text rounded-lg hover:bg-primary/10 transition-colors flex items-center gap-2">
            <ExternalLink size={16} />
            Push to Jira
          </button>
        </div>
      )}

      {/* Task Details Panel */}
      {selectedTask && (
        <div className="fixed inset-y-0 right-0 w-96 bg-background-light border-l border-primary/20 p-6 overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Task Details</h2>
            <button
              onClick={() => setSelectedTask(null)}
              className="p-2 hover:bg-primary/10 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-medium text-text-secondary mb-2">Description</h3>
              <p className="text-text">{selectedTask.description}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-text-secondary mb-2">Linked Evidence</h3>
              <div className="space-y-2">
                {selectedTask.evidence.map((file, index) => (
                  <div key={index} className="flex items-center gap-2 text-primary hover:text-primary-light">
                    <FileText size={16} />
                    <span>{file}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-text-secondary mb-2">Change Log</h3>
              <div className="space-y-2">
                {selectedTask.changelog.map((entry, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    <span className="text-text-secondary">{entry.date}</span>
                    <span className="text-text">{entry.action}</span>
                    <span className="text-text-secondary">by {entry.by}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {showToast && (
        <div className="fixed bottom-4 right-4 bg-background-light border border-primary/20 rounded-lg p-4 shadow-lg">
          <div className="flex items-center gap-2">
            <Check className="text-primary" size={20} />
            <span>Task marked resolved (score updated)</span>
          </div>
        </div>
      )}
    </div>
  );
} 